/**
 * Renders a sent `ask_user` (HITL) answer as a structured "Answered" block in
 * the user message bubble — so a reply is clearly distinguished from a normal
 * typed message and carries the chosen option's description (which the plain
 * answer text drops).
 *
 * Driven entirely by the message's `askUserRef` snapshot (questions + answers),
 * so it renders identically live and on reload. For each answered question we
 * resolve the answer `value` back to its option to show the option `label`
 * (bold) and `description` (muted); free-text / confirm answers fall back to the
 * raw value. Chrome (border, radius, header tint) matches `InlineAskUserCard`.
 */

import { CheckCircle2 } from 'lucide-react';
import type { AskUserAnswer, AskUserQuestion } from '@/types/inbox';

interface StructuredAnswerBlockProps {
  questions: AskUserQuestion[];
  answers: AskUserAnswer[];
}

/** One displayed value: the option label + its description when resolvable. */
function resolveValue(
  question: AskUserQuestion | undefined,
  value: string,
): { label: string; description?: string } {
  const opt = question?.options?.find((o) => o.label === value);
  return { label: value, description: opt?.description };
}

function AnsweredQuestion({
  question,
  answer,
  showHeader,
}: {
  question: AskUserQuestion | undefined;
  answer: AskUserAnswer;
  showHeader: boolean;
}) {
  const values = Array.isArray(answer.value) ? answer.value : [answer.value];
  const resolved = values.filter(Boolean).map((v) => resolveValue(question, v));
  const heading = question?.header || question?.question;

  return (
    <div className="space-y-1">
      {showHeader && heading ? (
        <div className="text-xs font-medium text-muted-foreground">{heading}</div>
      ) : null}
      {resolved.map((r, i) => (
        <div key={i}>
          <div className="text-sm font-medium text-foreground">{r.label}</div>
          {r.description ? (
            <div className="text-sm text-muted-foreground">{r.description}</div>
          ) : null}
        </div>
      ))}
      {answer.comment?.trim() ? (
        <div className="text-sm italic text-muted-foreground">
          “{answer.comment.trim()}”
        </div>
      ) : null}
    </div>
  );
}

export function StructuredAnswerBlock({
  questions,
  answers,
}: StructuredAnswerBlockProps) {
  if (!answers || answers.length === 0) return null;
  const byId = new Map(questions.map((q) => [q.questionId, q]));
  // Show per-question headers only when the reply spans more than one question.
  const showHeaders = answers.length > 1;

  return (
    <div className="col-start-2 overflow-hidden rounded-2xl border border-primary/30 bg-card">
      <div className="flex items-center gap-2 border-b border-border/60 bg-primary/5 px-3 py-1.5">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="text-xs font-medium text-foreground">Answered</span>
      </div>
      <div className="space-y-3 px-3 py-2.5">
        {answers.map((a, i) => (
          <AnsweredQuestion
            key={a.questionId || i}
            question={byId.get(a.questionId)}
            answer={a}
            showHeader={showHeaders}
          />
        ))}
      </div>
    </div>
  );
}
